import type {
  AccountUpdateJobInput,
  ApproveOrRejectJobInput,
  BulkMeterPointUpdateJobInput,
  BulkApiKeyUpdateJobInput,
  BulkApplicationGroupUpdateJobInput,
  BulkApplicationUpdateJobInput,
  BulkMeterUpdateJobInput,
  BulkUserInviteJobInput,
  BulkUserUpdateJobInput,
  BulkUtilityUpdateJobInput,
  DownloadJobInput,
  InsertUtilityDataMutation,
  ManualReadingUploadInput,
  MutationBase,
  RollbackInput,
  ScaleUtilityDataMutation,
  SearchJobsInput,
  SearchJobsOutput,
} from 'switch-shared/modules/crud/job/job.dto';
import { Job } from 'switch-shared/modules/crud/job/job.entity';

import { baseAPI, setToast } from './baseAPI.service';
import { API_METHODS, ENDPOINT_URLS } from './config/apiTags';

export const jobAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [ENDPOINT_URLS.JOB, ENDPOINT_URLS.ACCOUNT, 'Downloads'],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      searchJobs: builder.query<SearchJobsOutput, SearchJobsInput>({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.JOB}`,
            params,
          };
        },
        providesTags: (res, _err, _) => [
          { type: ENDPOINT_URLS.JOB, id: 'DOWNLOAD' },
          ...(res?.data.map((gen) => ({
            type: ENDPOINT_URLS.JOB,
            id: gen.id,
          })) ?? []),
        ],
      }),

      createDataScalingJob: builder.mutation<Job, ScaleUtilityDataMutation>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/util-scaling`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB],
      }),

      createDataDeletingJob: builder.mutation<Job, MutationBase>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/util-deleting`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB],
      }),
      createDataInsertingJob: builder.mutation<Job, InsertUtilityDataMutation>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/util-inserting`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB],
      }),
      rollbackJob: builder.mutation<Job, RollbackInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/rollback`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB],
      }),
      deleteJob: builder.mutation<{ success: boolean }, string>({
        query: (id) => ({
          url: `${ENDPOINT_URLS.JOB}/${id}`,
          method: API_METHODS.DELETE,
        }),
        invalidatesTags: (_res, _err, id) => [
          {
            type: ENDPOINT_URLS.JOB,
            id,
          },
        ],
      }),
      acceptOrDeclineJob: builder.mutation<Job, ApproveOrRejectJobInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/approveOrReject`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB],
      }),
      updateAccountInBulk: builder.mutation<void, AccountUpdateJobInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/bulkAccountUpdate`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [
          ENDPOINT_URLS.JOB,
          { type: ENDPOINT_URLS.ACCOUNT, id: 'TAGS' },
        ],
      }),
      updateMeterPointInBulk: builder.mutation<
        void,
        BulkMeterPointUpdateJobInput
      >({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/bulkMeterPointUpdate`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB],
      }),
      updateMeterInBulk: builder.mutation<void, BulkMeterUpdateJobInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/bulkMeterUpdate`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB],
      }),
      updateUserInBulk: builder.mutation<void, BulkUserUpdateJobInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/bulkUserUpdate`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB, ENDPOINT_URLS.USER],
      }),
      updateApplicationInBulk: builder.mutation<
        void,
        BulkApplicationUpdateJobInput
      >({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/bulkApplicationUpdate`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB, ENDPOINT_URLS.APPLICATION],
      }),
      updateApplicationGroupInBulk: builder.mutation<
        void,
        BulkApplicationGroupUpdateJobInput
      >({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/bulkApplicationGroupUpdate`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB, ENDPOINT_URLS.APPLICATION_GROUP],
      }),
      updateApiKeyInBulk: builder.mutation<void, BulkApiKeyUpdateJobInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/bulkApiKeyUpdate`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB, ENDPOINT_URLS.API_KEY],
      }),
      updateUtilityInBulk: builder.mutation<void, BulkUtilityUpdateJobInput>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.JOB}/bulkUtilityUpdate`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB],
      }),
      createManualReadingUploadJob: builder.mutation<
        void,
        ManualReadingUploadInput
      >({
        query: (body) => ({
          url: `${ENDPOINT_URLS.JOB}/manualReadingUpload`,
          method: API_METHODS.POST,
          body,
        }),
      }),
      createDownloadJob: builder.mutation<void, DownloadJobInput>({
        query: (body) => ({
          url: `${ENDPOINT_URLS.JOB}/download`,
          method: API_METHODS.POST,
          body,
        }),
        // TODO add tag per job type
        invalidatesTags: [{ type: ENDPOINT_URLS.JOB, id: 'DOWNLOAD' }],
      }),
      createBulkUserInvite: builder.mutation<Job, BulkUserInviteJobInput>({
        query: (body) => ({
          url: `${ENDPOINT_URLS.JOB}/bulkUserInvite`,
          method: API_METHODS.POST,
          body,
        }),
        invalidatesTags: [ENDPOINT_URLS.JOB],
      }),
      getJobById: builder.query<Job, string>({
        query: (id) => `${ENDPOINT_URLS.JOB}/${id}`,
        providesTags: (_res, _err, id) => [{ type: ENDPOINT_URLS.JOB, id }],
      }),
      getJobChildren: builder.query<Job[], string>({
        query: (id) => `${ENDPOINT_URLS.JOB}/${id}/children`,
        providesTags: (res) => [
          ...(res?.map((job) => ({ type: ENDPOINT_URLS.JOB, id: job.id })) ??
            []),
        ],
      }),
    }),
  });

[
  'updateAccountInBulk',
  'updateMeterPointInBulk',
  'updateUtilityInBulk',
  'updateUserInBulk',
  'updateApplicationInBulk',
  'updateApplicationGroupInBulk',
  'updateApiKeyInBulk',
].forEach((endpoint) =>
  setToast(endpoint, {
    pending: 'Starting bulk update...',
    fulfilled: 'Bulk update started. Changes appear once the job finishes.',
  })
);

setToast('createDownloadJob', {
  fulfilled:
    'Download is pending. Please see the downloads section in the main navigation bar',
  autoClose: 10000,
});

export const {
  useSearchJobsQuery,
  useCreateDataScalingJobMutation,
  useCreateDataDeletingJobMutation,
  useCreateDataInsertingJobMutation,
  useRollbackJobMutation,
  useDeleteJobMutation,
  useAcceptOrDeclineJobMutation,
  useUpdateAccountInBulkMutation,
  useUpdateMeterInBulkMutation,
  useUpdateMeterPointInBulkMutation,
  useUpdateUtilityInBulkMutation,
  useUpdateUserInBulkMutation,
  useUpdateApplicationInBulkMutation,
  useUpdateApplicationGroupInBulkMutation,
  useUpdateApiKeyInBulkMutation,
  useCreateManualReadingUploadJobMutation,
  useCreateDownloadJobMutation,
  useCreateBulkUserInviteMutation,
  useGetJobByIdQuery,
  useGetJobChildrenQuery,
} = jobAPI;
export default jobAPI;
